import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, Search, Filter, Calendar, Clock, User, 
  FolderKanban, ChevronRight, AlertTriangle, PauseCircle, UserCog, RefreshCw, Camera, Video, Plane, Edit3
} from 'lucide-react';
import { toast } from 'sonner';

const PROJECT_STATUSES = [
  { value: 'not_started', label: 'Not Started', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  { value: 'on_hold_shoot', label: 'On Hold - Shoot', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  { value: 'on_hold_client', label: 'On Hold - Client', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
  { value: 'on_hold_raw_files', label: 'On Hold - Raw Files', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
  { value: 'with_freelancer', label: 'With Freelancer', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  { value: 'freelancer_delivered', label: 'Freelancer Delivered', color: 'bg-amber-700/10 text-amber-600 border-amber-700/20' },
  { value: 'in_review', label: 'In Review', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
  { value: 'revision_requested', label: 'Revision Requested', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
  { value: 'delivered', label: 'Delivered', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
];

const KANBAN_COLUMNS = [
  { key: 'not_started', title: 'Not Started' },
  { key: 'in_progress', title: 'In Progress' },
  { key: 'on_hold', title: 'On Hold' },
  { key: 'with_freelancer', title: 'With Freelancer' },
  { key: 'in_review', title: 'In Review' },
  { key: 'delivered', title: 'Delivered' },
];

const ProjectsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [viewMode, setViewMode] = useState('months');
  const [openMonths, setOpenMonths] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newProject, setNewProject] = useState({
    client_name: '',
    title: '',
    brand: '',
    event_type: 'Wedding',
    shoot_date: '',
    delivery_deadline: '',
    assigned_to: '',
    notes: '',
    total_amount: '',
    events: [],
    advance_amount: '',
    advance_mode: 'upi',
    advance_account: '',
    advance_notes: '',
    advance_screenshot: null,
  });
  const [statusUpdate, setStatusUpdate] = useState({ status: '', hold_reason: '' });
  const [freelancers, setFreelancers] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [bookedLeads, setBookedLeads] = useState([]);
  const [assignProject, setAssignProject] = useState(null);
  const [assignForm, setAssignForm] = useState({
    freelancer_id: '',
    raw_files_handover_date: '',
    internal_deadline: '',
    agreed_amount: '',
    notes: ''
  });
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const isManagerOrAdmin = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));
      const canCreateProject = (user?.roles || [user?.role]).some(r => ['admin', 'manager', 'sales_person'].includes(r));

      const promises = [
        api.get('/projects'),
        api.get('/clients'),
        api.get('/users/team')
      ];
      if (isManagerOrAdmin) promises.push(api.get('/freelancers'));
      if (canCreateProject) promises.push(api.get('/projects/booked-leads'));

      const results = await Promise.all(promises);
      setProjects(results[0].data);
      setClients(results[1].data);
      setTeamMembers(results[2].data);

      let idx = 3;
      if (isManagerOrAdmin) { setFreelancers(results[idx]?.data || []); idx++; }
      if (canCreateProject) { setBookedLeads((results[idx]?.data || []).filter(l => !l.has_project)); }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.client_name?.trim()) {
      toast.error('Please enter client name');
      return;
    }
    if (!newProject.title.trim()) {
      toast.error('Please enter a project title');
      return;
    }
    if (!newProject.brand) {
      toast.error('Please select a brand (Wittyshadows or Trio Bliss)');
      return;
    }
    try {
      const payload = {
        client_name: newProject.client_name.trim(),
        title: newProject.title.trim(),
        brand: newProject.brand,
        event_type: newProject.event_type,
        shoot_date: newProject.shoot_date ? new Date(newProject.shoot_date).toISOString() : new Date().toISOString(),
        delivery_deadline: newProject.delivery_deadline ? new Date(newProject.delivery_deadline).toISOString() : new Date(Date.now() + 45 * 86400000).toISOString(),
        total_amount: parseFloat(newProject.total_amount) || 0,
        notes: newProject.notes,
        assigned_to: newProject.assigned_to || null,
        events: newProject.events.filter(ev => ev.event_name && ev.date).map(ev => ({
          ...ev,
          date: new Date(ev.date).toISOString(),
          num_photographers: parseInt(ev.num_photographers) || 0,
          num_videographers: parseInt(ev.num_videographers) || 0,
        })),
        advance_amount: parseFloat(newProject.advance_amount) || 0,
        advance_mode: newProject.advance_mode || 'upi',
        advance_account: newProject.advance_account || null,
        advance_notes: newProject.advance_notes || null,
      };
      const projRes = await api.post('/projects', payload);
      // Upload advance screenshot (if provided) on the newly-created advance payment
      if (payload.advance_amount > 0 && newProject.advance_screenshot && projRes.data?.id) {
        try {
          // Fetch payments for the client to locate the latest payment id
          const proj = projRes.data;
          const paysRes = await api.get(`/accounts/${proj.client_id || ''}/payments`).catch(() => null);
          const paymentId = paysRes?.data?.[0]?.id;
          if (paymentId) {
            const formData = new FormData();
            formData.append('file', newProject.advance_screenshot);
            await api.post(`/accounts/${proj.client_id}/payments/${paymentId}/screenshot`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
        } catch (e) {
          console.error('Advance screenshot upload failed (non-fatal):', e);
        }
      }
      toast.success('Project created successfully');
      setShowCreateDialog(false);
      setNewProject({
        client_name: '', title: '', brand: '', event_type: 'Wedding',
        shoot_date: '', delivery_deadline: '', assigned_to: '', notes: '',
        total_amount: '', events: [],
        advance_amount: '', advance_mode: 'upi', advance_account: '', advance_notes: '', advance_screenshot: null,
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedProject) return;
    try {
      await api.patch(`/projects/${selectedProject.id}`, statusUpdate);
      toast.success('Status updated successfully');
      setShowStatusDialog(false);
      setStatusUpdate({ status: '', hold_reason: '' });
      setSelectedProject(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // ===== Edit Project =====
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editProject, setEditProject] = useState(null);
  
  const openEditDialog = (project) => {
    setEditProject({
      id: project.id,
      title: project.title || '',
      client_name: project.client_name || '',
      brand: project.brand || 'wittyshadows',
      event_type: project.event_type || 'Wedding',
      shoot_date: project.shoot_date ? new Date(project.shoot_date).toISOString().slice(0, 16) : '',
      delivery_deadline: project.delivery_deadline ? new Date(project.delivery_deadline).toISOString().slice(0, 16) : '',
      total_amount: project.total_amount || 0,
      advance_amount: project.advance_amount || 0,
      account: project.account || 'wittyshadows_current',
      notes: project.notes || '',
    });
    setShowEditDialog(true);
  };
  
  const handleEditProjectSave = async () => {
    if (!editProject) return;
    try {
      const payload = {
        title: editProject.title,
        client_name: editProject.client_name,
        brand: editProject.brand,
        event_type: editProject.event_type,
        total_amount: parseFloat(editProject.total_amount) || 0,
        advance_amount: parseFloat(editProject.advance_amount) || 0,
        account: editProject.account,
        notes: editProject.notes,
      };
      if (editProject.shoot_date) payload.shoot_date = new Date(editProject.shoot_date).toISOString();
      if (editProject.delivery_deadline) payload.delivery_deadline = new Date(editProject.delivery_deadline).toISOString();
      await api.patch(`/projects/${editProject.id}`, payload);
      toast.success('Project updated');
      setShowEditDialog(false);
      setEditProject(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update project');
    }
  };

  const handleAssignFreelancer = async () => {
    if (!assignForm.freelancer_id || !assignForm.raw_files_handover_date || !assignForm.internal_deadline || !assignForm.agreed_amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await api.post(`/projects/${assignProject.id}/assign-freelancer`, {
        freelancer_id: assignForm.freelancer_id,
        raw_files_handover_date: new Date(assignForm.raw_files_handover_date).toISOString(),
        internal_deadline: new Date(assignForm.internal_deadline).toISOString(),
        agreed_amount: parseFloat(assignForm.agreed_amount),
        notes: assignForm.notes || null
      });
      toast.success('Freelancer assigned successfully');
      setShowAssignDialog(false);
      setAssignForm({ freelancer_id: '', raw_files_handover_date: '', internal_deadline: '', agreed_amount: '', notes: '' });
      setAssignProject(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign freelancer');
    }
  };

  const getStatusColor = (status) => {
    const statusObj = PROJECT_STATUSES.find(s => s.value === status);
    return statusObj?.color || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
  };

  const getRiskBadge = (risk) => {
    const badges = {
      low: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      high: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      critical: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
      overdue: 'bg-rose-500/10 text-rose-400 border-rose-500/20 badge-overdue',
      completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
    };
    return badges[risk] || badges.low;
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.client_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchesBrand = brandFilter === 'all' || (p.brand || 'wittyshadows') === brandFilter;
    return matchesSearch && matchesStatus && matchesBrand;
  });

  const monthKey = (p) => { const d = p.shoot_date || p.delivery_deadline || p.created_at; return d ? String(d).slice(0, 7) : 'none'; };
  const monthLabel = (k) => k === 'none' ? 'No date' : new Date(k + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const monthGroups = Object.entries(filteredProjects.reduce((a, p) => { (a[monthKey(p)] = a[monthKey(p)] || []).push(p); return a; }, {}))
    .sort((a, b) => a[0] === 'none' ? 1 : b[0] === 'none' ? -1 : b[0].localeCompare(a[0]))
    .map(([k, list]) => [k, [...list].sort((x, y) => String(x.shoot_date || '').localeCompare(String(y.shoot_date || '')))]);

  const getKanbanProjects = (columnKey) => {
    return filteredProjects.filter(p => {
      if (columnKey === 'on_hold') {
        return p.status.startsWith('on_hold');
      }
      if (columnKey === 'with_freelancer') {
        return p.status === 'with_freelancer' || p.status === 'freelancer_delivered';
      }
      if (columnKey === 'in_review') {
        return p.status === 'in_review' || p.status === 'revision_requested';
      }
      return p.status === columnKey;
    });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Projects</h1>
          <p className="text-zinc-500 text-sm mt-1">{projects.length} total projects</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}
            className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-projects">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          {(user?.roles || [user?.role]).some(r => ['admin', 'manager', 'sales_person'].includes(r)) && (
            <Button onClick={() => setShowCreateDialog(true)} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="create-project-btn">
              <Plus className="h-4 w-4 mr-2" /> New Project
            </Button>
          )}
        </div>
      </div>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Brand */}
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Brand *</Label>
                  <div className="flex gap-2 mt-1">
                    {[{v: 'wittyshadows', l: 'Wittyshadows'}, {v: 'triobliss', l: 'Trio Bliss'}].map(b => (
                      <button key={b.v} type="button" onClick={() => setNewProject({...newProject, brand: b.v})}
                        className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-colors ${
                          newProject.brand === b.v
                            ? b.v === 'triobliss' ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30' : 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30'
                            : 'bg-[#0A0A0A] text-zinc-400 border border-white/10 hover:border-white/20'
                        }`} data-testid={`brand-${b.v}`}>
                        {b.l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Client Name *</Label>
                    <Input value={newProject.client_name} onChange={(e) => setNewProject({...newProject, client_name: e.target.value})}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="e.g., Sharma Family" data-testid="client-name-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Project Title *</Label>
                    <Input value={newProject.title} onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="e.g., Sharma Wedding" data-testid="project-title-input" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Event Type</Label>
                    <Select value={newProject.event_type} onValueChange={(v) => setNewProject({...newProject, event_type: v})}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {['Wedding', 'Pre-Wedding', 'Corporate', 'Birthday', 'Other'].map(t => (
                          <SelectItem key={t} value={t} className="text-white hover:bg-white/10">{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Shoot Date</Label>
                    <Input type="date" value={newProject.shoot_date} onChange={(e) => setNewProject({...newProject, shoot_date: e.target.value})}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="shoot-date-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Delivery Deadline</Label>
                    <Input type="date" value={newProject.delivery_deadline} onChange={(e) => setNewProject({...newProject, delivery_deadline: e.target.value})}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="deadline-input" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Total Amount (&#8377;)</Label>
                    <Input type="number" value={newProject.total_amount} onChange={(e) => setNewProject({...newProject, total_amount: e.target.value})}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="150000" data-testid="total-amount-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Assign To</Label>
                    <Select value={newProject.assigned_to} onValueChange={(v) => setNewProject({...newProject, assigned_to: v})}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {teamMembers.map(m => (
                          <SelectItem key={m.id} value={m.id} className="text-white hover:bg-white/10">{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Advance Received Section */}
                <div className="border border-[#F5A623]/20 rounded-lg p-3 bg-[#F5A623]/5" data-testid="advance-section">
                  <Label className="text-[#F5A623] text-xs uppercase tracking-wider">Advance Received (Optional)</Label>
                  <p className="text-zinc-500 text-[11px] mt-0.5 mb-3">Record an advance payment at project creation. Leave blank to skip.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-[11px]">Amount (&#8377;)</Label>
                      <Input type="number" value={newProject.advance_amount}
                        onChange={(e) => setNewProject({...newProject, advance_amount: e.target.value})}
                        className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="50000"
                        data-testid="advance-amount-input" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-[11px]">Mode</Label>
                      <div className="flex gap-1.5 mt-1">
                        {['upi', 'cash'].map(m => (
                          <button key={m} type="button"
                            onClick={() => setNewProject({...newProject, advance_mode: m})}
                            className={`flex-1 px-2 py-1.5 rounded border text-xs capitalize transition-colors ${
                              newProject.advance_mode === m
                                ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]'
                                : 'border-white/10 text-zinc-400 hover:border-white/30'
                            }`}
                            data-testid={`advance-mode-${m}`}>{m}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-zinc-400 text-[11px]">Received In Account</Label>
                    <div className="flex gap-1.5 mt-1 flex-wrap">
                      {['Shubham', 'Lucky', 'Pankaj'].map(acc => (
                        <button key={acc} type="button"
                          onClick={() => setNewProject({...newProject, advance_account: acc})}
                          className={`px-3 py-1.5 rounded border text-xs transition-colors ${
                            newProject.advance_account === acc
                              ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]'
                              : 'border-white/10 text-zinc-400 hover:border-white/30'
                          }`}
                          data-testid={`advance-account-${acc}`}>{acc}</button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-zinc-400 text-[11px]">Screenshot (Optional)</Label>
                    <Input type="file" accept="image/*"
                      onChange={(e) => setNewProject({...newProject, advance_screenshot: e.target.files?.[0] || null})}
                      className="bg-[#0A0A0A] border-white/10 text-zinc-300 mt-1 text-xs file:bg-[#F5A623]/10 file:text-[#F5A623] file:border-0 file:px-3 file:py-1 file:rounded file:mr-3"
                      data-testid="advance-screenshot-input" />
                    {newProject.advance_screenshot && (
                      <p className="text-emerald-400 text-[10px] mt-1">✓ {newProject.advance_screenshot.name}</p>
                    )}
                  </div>
                  <div className="mt-3">
                    <Label className="text-zinc-400 text-[11px]">Notes</Label>
                    <Input value={newProject.advance_notes}
                      onChange={(e) => setNewProject({...newProject, advance_notes: e.target.value})}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1 text-xs"
                      placeholder="e.g. Advance via Google Pay"
                      data-testid="advance-notes-input" />
                  </div>
                </div>
                <div className="border border-white/10 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Event Dates & Team</Label>
                    <Button type="button" variant="outline" size="sm"
                      className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10 text-xs h-7"
                      onClick={() => setNewProject(prev => ({
                        ...prev, events: [...prev.events, { event_name: '', date: '', timing: 'day', team: [], freelancers: [], num_photographers: '', num_videographers: '', drone: false, special_requirements: '' }]
                      }))} data-testid="add-event-btn">
                      <Plus className="h-3 w-3 mr-1" /> Add Event
                    </Button>
                  </div>
                  {newProject.events.length === 0 && (
                    <p className="text-xs text-zinc-500 text-center py-3">No events added yet. Click "Add Event" above.</p>
                  )}
                  {newProject.events.map((ev, idx) => (
                    <div key={idx} className="p-3 bg-[#0A0A0A] rounded-md border border-white/5 mb-2">
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <Input value={ev.event_name} onChange={e => {
                          const evs = [...newProject.events]; evs[idx] = {...evs[idx], event_name: e.target.value};
                          setNewProject({...newProject, events: evs});
                        }} className="bg-[#121212] border-white/10 text-white text-sm" placeholder="Event name (Mehendi, Wedding...)" />
                        <Input type="date" value={ev.date} onChange={e => {
                          const evs = [...newProject.events]; evs[idx] = {...evs[idx], date: e.target.value};
                          setNewProject({...newProject, events: evs});
                        }} className="bg-[#121212] border-white/10 text-white text-sm" />
                        <div className="flex gap-1">
                          {['day', 'night'].map(t => (
                            <button key={t} type="button" onClick={() => {
                              const evs = [...newProject.events]; evs[idx] = {...evs[idx], timing: t};
                              setNewProject({...newProject, events: evs});
                            }} className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors ${
                              ev.timing === t ? (t === 'night' ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30')
                              : 'bg-[#121212] text-zinc-500 border border-white/10'
                            }`}>
                              {t === 'day' ? 'Day' : 'Night'}
                            </button>
                          ))}
                          <button type="button" onClick={() => {
                            setNewProject({...newProject, events: newProject.events.filter((_, i) => i !== idx)});
                          }} className="px-2 text-rose-400 hover:bg-rose-500/10 rounded transition-colors text-xs">x</button>
                        </div>
                      </div>
                      {/* Crew per event */}
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        <div>
                          <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-0.5"><Camera className="h-2.5 w-2.5" /> Photographers</div>
                          <Input type="number" min="0" value={ev.num_photographers || ''} onChange={e => {
                            const evs = [...newProject.events]; evs[idx] = {...evs[idx], num_photographers: e.target.value};
                            setNewProject({...newProject, events: evs});
                          }} className="bg-[#121212] border-white/10 text-white h-7 text-xs" placeholder="0" />
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-0.5"><Video className="h-2.5 w-2.5" /> Videographers</div>
                          <Input type="number" min="0" value={ev.num_videographers || ''} onChange={e => {
                            const evs = [...newProject.events]; evs[idx] = {...evs[idx], num_videographers: e.target.value};
                            setNewProject({...newProject, events: evs});
                          }} className="bg-[#121212] border-white/10 text-white h-7 text-xs" placeholder="0" />
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-0.5"><Plane className="h-2.5 w-2.5" /> Drone</div>
                          <button type="button" onClick={() => {
                            const evs = [...newProject.events]; evs[idx] = {...evs[idx], drone: !evs[idx].drone};
                            setNewProject({...newProject, events: evs});
                          }} className={`w-full h-7 rounded text-[10px] font-medium transition-colors ${
                            ev.drone ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[#121212] text-zinc-500 border border-white/10'
                          }`}>{ev.drone ? 'Yes' : 'No'}</button>
                        </div>
                        <div>
                          <div className="text-[10px] text-zinc-500 mb-0.5">Special Req.</div>
                          <Input value={ev.special_requirements || ''} onChange={e => {
                            const evs = [...newProject.events]; evs[idx] = {...evs[idx], special_requirements: e.target.value};
                            setNewProject({...newProject, events: evs});
                          }} className="bg-[#121212] border-white/10 text-white h-7 text-xs" placeholder="Notes..." />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-[10px] text-zinc-500 mb-1">Team Members</p>
                          <Select onValueChange={(v) => {
                            const member = teamMembers.find(m => m.id === v);
                            if (member && !ev.team.find(t => t.user_id === v)) {
                              const evs = [...newProject.events];
                              evs[idx] = {...evs[idx], team: [...evs[idx].team, { user_id: v, name: member.name }]};
                              setNewProject({...newProject, events: evs});
                            }
                          }}>
                            <SelectTrigger className="bg-[#121212] border-white/10 text-white text-xs h-8">
                              <SelectValue placeholder="+ Add team" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-white/10">
                              {teamMembers.map(m => (
                                <SelectItem key={m.id} value={m.id} className="text-white hover:bg-white/10 text-xs">{m.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {ev.team.map(t => (
                              <Badge key={t.user_id} variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20 cursor-pointer"
                                onClick={() => {
                                  const evs = [...newProject.events];
                                  evs[idx] = {...evs[idx], team: evs[idx].team.filter(x => x.user_id !== t.user_id)};
                                  setNewProject({...newProject, events: evs});
                                }}>
                                {t.name} x
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] text-zinc-500 mb-1">Freelancers</p>
                          <Select onValueChange={(v) => {
                            const fr = freelancers.find(f => f.id === v);
                            if (fr && !ev.freelancers.find(f => f.user_id === v)) {
                              const evs = [...newProject.events];
                              evs[idx] = {...evs[idx], freelancers: [...evs[idx].freelancers, { user_id: v, name: fr.name }]};
                              setNewProject({...newProject, events: evs});
                            }
                          }}>
                            <SelectTrigger className="bg-[#121212] border-white/10 text-white text-xs h-8">
                              <SelectValue placeholder="+ Add freelancer" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-white/10">
                              {freelancers.map(f => (
                                <SelectItem key={f.id} value={f.id} className="text-white hover:bg-white/10 text-xs">{f.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {ev.freelancers.map(f => (
                              <Badge key={f.user_id} variant="outline" className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/20 cursor-pointer"
                                onClick={() => {
                                  const evs = [...newProject.events];
                                  evs[idx] = {...evs[idx], freelancers: evs[idx].freelancers.filter(x => x.user_id !== f.user_id)};
                                  setNewProject({...newProject, events: evs});
                                }}>
                                {f.name} x
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
                  <Textarea
                    value={newProject.notes}
                    onChange={(e) => setNewProject({...newProject, notes: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="Any special instructions..."
                    rows={2}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                    Cancel
                  </Button>
                  <Button onClick={handleCreateProject} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-project-btn">
                    Create Project
                  </Button>
                </div>
              </div>
            </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500"
            data-testid="search-projects-input"
          />
        </div>
        <Select value={brandFilter} onValueChange={setBrandFilter}>
          <SelectTrigger className="w-full sm:w-44 bg-[#121212] border-white/10 text-white" data-testid="brand-filter">
            <SelectValue placeholder="Brand" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">All Brands</SelectItem>
            <SelectItem value="wittyshadows" className="text-white hover:bg-white/10">Wittyshadows</SelectItem>
            <SelectItem value="triobliss" className="text-white hover:bg-white/10">Trio Bliss</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-[#121212] border-white/10 text-white">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">All Statuses</SelectItem>
            {PROJECT_STATUSES.map(s => (
              <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/10">{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          <Button size="sm"
            variant={viewMode === 'months' ? 'default' : 'outline'}
            onClick={() => setViewMode('months')}
            className={viewMode === 'months' ? 'bg-[#F5A623] text-black' : 'border-white/10 text-white hover:bg-white/5'}
            data-testid="months-view-btn"
          >
            Months
          </Button>
          <Button size="sm"
            variant={viewMode === 'kanban' ? 'default' : 'outline'}
            onClick={() => setViewMode('kanban')}
            className={viewMode === 'kanban' ? 'bg-[#F5A623] text-black' : 'border-white/10 text-white hover:bg-white/5'}
            data-testid="kanban-view-btn"
          >
            <FolderKanban className="h-4 w-4" />
          </Button>
          <Button size="sm"
            variant={viewMode === 'list' ? 'default' : 'outline'}
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'bg-[#F5A623] text-black' : 'border-white/10 text-white hover:bg-white/5'}
            data-testid="list-view-btn"
          >
            <Calendar className="h-4 w-4" />
          </Button>
          <Button size="sm"
            variant={viewMode === 'timeline' ? 'default' : 'outline'}
            onClick={() => setViewMode('timeline')}
            className={viewMode === 'timeline' ? 'bg-[#F5A623] text-black' : 'border-white/10 text-white hover:bg-white/5'}
            data-testid="timeline-view-btn"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {viewMode === 'months' && (
        <div className="space-y-3" data-testid="months-view">
          {monthGroups.length === 0 && <div className="p-6 border border-dashed border-white/10 rounded text-center text-zinc-600 text-sm">No projects</div>}
          {monthGroups.map(([key, list], idx) => {
            const isOpen = openMonths[key] ?? idx < 2;
            const overdue = list.filter(p => p.risk_level === 'overdue' && p.status !== 'delivered').length;
            const delivered = list.filter(p => p.status === 'delivered').length;
            return (
              <Card key={key} className="bg-[#121212] border-white/10">
                <button type="button" onClick={() => setOpenMonths(m => ({ ...m, [key]: !isOpen }))}
                  className="w-full flex items-center justify-between p-4 text-left" data-testid={`month-${key}`}>
                  <span className="flex items-center gap-3">
                    <ChevronRight className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    <span className="text-white font-semibold">{monthLabel(key)}</span>
                    <Badge variant="outline" className="bg-white/5 text-zinc-400 border-white/10">{list.length} projects</Badge>
                  </span>
                  <span className="flex items-center gap-3 text-xs">
                    {overdue > 0 && <span className="text-rose-400">{overdue} overdue</span>}
                    <span className="text-emerald-400">{delivered} delivered</span>
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-white/10 divide-y divide-white/5">
                    {list.map(project => (
                      <div key={project.id} onClick={() => navigate(`/projects/${project.id}`)}
                        className="grid grid-cols-12 gap-2 items-center px-4 py-3 cursor-pointer hover:bg-white/5 text-sm"
                        data-testid={`month-row-${project.id}`}>
                        <span className="col-span-5 text-white truncate">{project.title}</span>
                        <span className="col-span-2 text-zinc-500 text-xs truncate">{project.client_name}</span>
                        <span className="col-span-2 text-zinc-400 text-xs">{formatDate(project.shoot_date)}</span>
                        <span className="col-span-2 text-xs text-zinc-300">{PROJECT_STATUSES.find(s => s.value === project.status)?.label || project.status}</span>
                        <span className="col-span-1 text-[10px] text-zinc-500">{(project.brand || 'wittyshadows') === 'triobliss' ? 'TB' : 'WS'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 overflow-x-auto" data-testid="kanban-board">
          {KANBAN_COLUMNS.map(column => (
            <div key={column.key} className="min-w-[280px]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">{column.title}</h3>
                <Badge variant="outline" className="bg-white/5 text-zinc-400 border-white/10">
                  {getKanbanProjects(column.key).length}
                </Badge>
              </div>
              <div className="space-y-3">
                {getKanbanProjects(column.key).map(project => (
                  <Card 
                    key={project.id} 
                    className="bg-[#121212] border-white/10 card-hover cursor-pointer"
                    onClick={() => navigate(`/projects/${project.id}`)}
                    data-testid={`kanban-card-${project.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="text-white font-medium text-sm truncate">{project.title}</h4>
                        <Badge variant="outline" className={`text-xs shrink-0 ${getRiskBadge(project.risk_level)}`}>
                          {project.risk_level === 'overdue' ? 'OVERDUE' : project.risk_level.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="text-xs text-zinc-500 mb-3 flex items-center gap-1">
                        <span>{project.client_name}</span>
                        <Badge variant="outline" className={`text-[9px] ${(project.brand || 'wittyshadows') === 'triobliss' ? 'bg-pink-500/10 text-pink-400 border-pink-500/20' : 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20'}`}>
                          {(project.brand || 'wittyshadows') === 'triobliss' ? 'Trio Bliss' : 'WS'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center justify-between text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> Shoot
                          </span>
                          <span className="text-zinc-400">{formatDate(project.shoot_date)}</span>
                        </div>
                        <div className="flex items-center justify-between text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" /> Deadline
                          </span>
                          <span className={project.days_remaining < 0 ? 'text-rose-400' : 'text-zinc-400'}>
                            {formatDate(project.delivery_deadline)}
                          </span>
                        </div>
                        {project.assigned_to_name && (
                          <div className="flex items-center justify-between text-zinc-500">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" /> Editor
                            </span>
                            <span className="text-zinc-400">{project.assigned_to_name}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3">
                        <Progress value={project.progress_percent} className="h-1 bg-white/5" />
                        <div className="flex justify-between mt-1 text-xs text-zinc-500">
                          <span>{project.days_elapsed}d elapsed</span>
                          <span>{project.days_remaining > 0 ? `${project.days_remaining}d left` : 'Overdue'}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {getKanbanProjects(column.key).length === 0 && (
                  <div className="p-4 border border-dashed border-white/10 rounded text-center text-zinc-600 text-sm">
                    No projects
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Project</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Brand</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Client</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Status</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Deadline</th>
                    <th className="text-right p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Amount</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Assigned</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map(project => (
                    <tr 
                      key={project.id} 
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                      onClick={() => navigate(`/projects/${project.id}`)}
                      data-testid={`list-row-${project.id}`}
                    >
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{project.title}</p>
                          <p className="text-xs text-zinc-500">{project.event_type}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className={`text-[10px] ${(project.brand || 'wittyshadows') === 'triobliss' ? 'bg-pink-500/10 text-pink-400 border-pink-500/20' : 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20'}`}>
                          {(project.brand || 'wittyshadows') === 'triobliss' ? 'Trio Bliss' : 'Wittyshadows'}
                        </Badge>
                      </td>
                      <td className="p-4 text-zinc-400">{project.client_name}</td>
                      <td className="p-4">
                        <Badge variant="outline" className={getStatusColor(project.status)}>
                          {PROJECT_STATUSES.find(s => s.value === project.status)?.label || project.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className={project.days_remaining < 0 ? 'text-rose-400' : 'text-zinc-400'}>
                            {formatDate(project.delivery_deadline)}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {project.days_remaining > 0 ? `${project.days_remaining}d left` : 
                             project.days_remaining === 0 ? 'Due today' : 
                             `${Math.abs(project.days_remaining)}d overdue`}
                          </p>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        {project.total_amount > 0 ? (
                          <span className="text-white text-sm">&#8377;{project.total_amount.toLocaleString('en-IN')}</span>
                        ) : (
                          <span className="text-zinc-600 text-sm">—</span>
                        )}
                      </td>
                      <td className="p-4 text-zinc-400">{project.assigned_to_name || '-'}</td>
                      <td className="p-2 text-right">
                        {(user?.roles || []).some(r => ['admin', 'manager', 'sales_person'].includes(r)) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditDialog(project); }}
                            className="text-zinc-400 hover:text-[#F5A623] p-1"
                            data-testid={`edit-project-${project.id}`}
                            title="Edit project">
                            <Edit3 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline / Gantt View */}
      {viewMode === 'timeline' && (
        <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden" data-testid="timeline-view">
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Timeline Header */}
              {(() => {
                const now = new Date();
                const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                const endDate = new Date(now.getFullYear(), now.getMonth() + 3, 0);
                const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                const months = [];
                let d = new Date(startDate);
                while (d <= endDate) {
                  months.push({ month: d.toLocaleString('en-IN', { month: 'short' }), year: d.getFullYear(), days: new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate() });
                  d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
                }
                const dayWidth = Math.max(100 / totalDays, 0.8);
                const getPosition = (date) => {
                  if (!date) return { left: 0, width: 0 };
                  const dt = new Date(date);
                  const diff = Math.max(0, (dt - startDate) / (1000 * 60 * 60 * 24));
                  return diff * dayWidth;
                };
                const todayPos = getPosition(now);

                return (
                  <>
                    {/* Month headers */}
                    <div className="flex border-b border-white/10">
                      <div className="w-48 shrink-0 px-4 py-2 bg-[#0A0A0A] border-r border-white/10">
                        <span className="text-xs text-zinc-500 uppercase">Project</span>
                      </div>
                      <div className="flex-1 flex relative">
                        {months.map((m, i) => (
                          <div key={i} className="text-center text-xs text-zinc-400 py-2 border-r border-white/5" style={{ width: `${(m.days / totalDays) * 100}%` }}>
                            {m.month} {m.year}
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Project rows */}
                    {filteredProjects.map(project => {
                      const shootLeft = getPosition(project.shoot_date);
                      const deadlineLeft = getPosition(project.delivery_deadline);
                      const barStart = Math.min(shootLeft, deadlineLeft);
                      const barWidth = Math.max(Math.abs(deadlineLeft - shootLeft), 2);
                      const isTB = (project.brand || 'wittyshadows') === 'triobliss';

                      return (
                        <div key={project.id} className="flex border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)} data-testid={`timeline-row-${project.id}`}>
                          <div className="w-48 shrink-0 px-3 py-2 bg-[#0A0A0A]/50 border-r border-white/10">
                            <p className="text-xs text-white font-medium truncate">{project.title}</p>
                            <p className="text-[10px] text-zinc-500 truncate">{project.client_name}</p>
                          </div>
                          <div className="flex-1 relative py-2 px-1">
                            {/* Bar */}
                            <div className={`absolute top-1/2 -translate-y-1/2 h-5 rounded-full ${isTB ? 'bg-pink-500/30 border border-pink-500/40' : 'bg-[#F5A623]/30 border border-[#F5A623]/40'}`}
                              style={{ left: `${barStart}%`, width: `${barWidth}%`, minWidth: '20px' }}>
                              <div className={`h-full rounded-full ${project.status === 'done' ? 'bg-emerald-500/60' : isTB ? 'bg-pink-500/60' : 'bg-[#F5A623]/60'}`}
                                style={{ width: `${project.progress_percent}%` }} />
                            </div>
                            {/* Events dots */}
                            {(project.events || []).map((ev, i) => {
                              const evPos = getPosition(ev.date);
                              return (
                                <div key={i} className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white border border-white/50 z-10"
                                  style={{ left: `${evPos}%` }} title={`${ev.event_name} - ${ev.timing}`} />
                              );
                            })}
                            {/* Today marker */}
                            <div className="absolute top-0 bottom-0 w-px bg-rose-500/50 z-20" style={{ left: `${todayPos}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    {/* Today label */}
                    <div className="flex">
                      <div className="w-48 shrink-0" />
                      <div className="flex-1 relative h-6">
                        <div className="absolute text-[9px] text-rose-400 -translate-x-1/2" style={{ left: `${todayPos}%` }}>Today</div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Update Project Status</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#0A0A0A] rounded border border-white/5">
                <p className="text-white font-medium">{selectedProject.title}</p>
                <p className="text-sm text-zinc-500">{selectedProject.client_name}</p>
              </div>
              
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">New Status</Label>
                <Select value={statusUpdate.status} onValueChange={(v) => setStatusUpdate({...statusUpdate, status: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {PROJECT_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/10">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {statusUpdate.status.startsWith('on_hold') && (
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Hold Reason (Required)</Label>
                  <Textarea
                    value={statusUpdate.hold_reason}
                    onChange={(e) => setStatusUpdate({...statusUpdate, hold_reason: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="Why is this project on hold?"
                    rows={3}
                    data-testid="hold-reason-input"
                  />
                </div>
              )}

              <div className="flex justify-between items-center pt-4">
                {(user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r)) && selectedProject.status !== 'with_freelancer' && (
                  <Button
                    variant="outline"
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                    onClick={() => {
                      setAssignProject(selectedProject);
                      setShowStatusDialog(false);
                      setShowAssignDialog(true);
                      // Fetch suggestions
                      api.get(`/projects/${selectedProject.id}/suggest-freelancers`).then(res => setSuggestions(res.data)).catch(() => {});
                    }}
                    data-testid="assign-freelancer-btn"
                  >
                    <UserCog className="h-4 w-4 mr-2" /> Assign Freelancer
                  </Button>
                )}
                <div className="flex gap-3 ml-auto">
                  <Button variant="outline" onClick={() => setShowStatusDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpdateStatus} 
                    className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                    disabled={statusUpdate.status.startsWith('on_hold') && !statusUpdate.hold_reason}
                    data-testid="update-status-btn"
                  >
                    Update Status
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(o) => { setShowEditDialog(o); if (!o) setEditProject(null); }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          {editProject && (
            <div className="space-y-3" data-testid="edit-project-dialog">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Title</Label>
                  <Input value={editProject.title} onChange={(e) => setEditProject({ ...editProject, title: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10" data-testid="edit-title" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Client Name</Label>
                  <Input value={editProject.client_name} onChange={(e) => setEditProject({ ...editProject, client_name: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10" data-testid="edit-client" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-zinc-400">Brand</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[{ k: 'wittyshadows', l: 'Wittyshadows' }, { k: 'triobliss', l: 'Trio Bliss' }].map(b => (
                    <button key={b.k} type="button"
                      onClick={() => setEditProject({ ...editProject, brand: b.k })}
                      className={`px-3 py-2 rounded border text-sm ${editProject.brand === b.k ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-white/10 bg-white/5 text-white/70'}`}
                      data-testid={`edit-brand-${b.k}`}>
                      {b.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Event Type</Label>
                  <Input value={editProject.event_type} onChange={(e) => setEditProject({ ...editProject, event_type: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Account</Label>
                  <Input value={editProject.account} onChange={(e) => setEditProject({ ...editProject, account: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Shoot Date</Label>
                  <Input type="datetime-local" value={editProject.shoot_date}
                    onChange={(e) => setEditProject({ ...editProject, shoot_date: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Delivery Deadline</Label>
                  <Input type="datetime-local" value={editProject.delivery_deadline}
                    onChange={(e) => setEditProject({ ...editProject, delivery_deadline: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Total Amount (₹)</Label>
                  <Input type="number" value={editProject.total_amount}
                    onChange={(e) => setEditProject({ ...editProject, total_amount: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Advance Amount (₹)</Label>
                  <Input type="number" value={editProject.advance_amount}
                    onChange={(e) => setEditProject({ ...editProject, advance_amount: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-zinc-400">Notes</Label>
                <Textarea value={editProject.notes} onChange={(e) => setEditProject({ ...editProject, notes: e.target.value })}
                  rows={2} className="bg-[#0A0A0A] border-white/10" />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditProject(null); }} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                <Button onClick={handleEditProjectSave} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="save-edit-project">
                  Save changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Freelancer Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Freelancer to Project</DialogTitle>
          </DialogHeader>
          {assignProject && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#0A0A0A] rounded border border-white/5">
                <p className="text-white font-medium">{assignProject.title}</p>
                <p className="text-sm text-zinc-500">{assignProject.client_name}</p>
              </div>

              {/* AI Suggestions */}
              {suggestions.length > 0 && (
                <div data-testid="freelancer-suggestions">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Recommended Freelancers</Label>
                  <div className="space-y-2">
                    {suggestions.slice(0, 3).map((s, i) => (
                      <div key={s.id} className={`flex items-center justify-between p-3 rounded border cursor-pointer transition-colors ${assignForm.freelancer_id === s.id ? 'bg-[#F5A623]/10 border-[#F5A623]/30' : 'bg-[#0A0A0A] border-white/5 hover:border-[#F5A623]/20'}`}
                        onClick={() => setAssignForm({...assignForm, freelancer_id: s.id, agreed_amount: s.rate_per_project ? String(s.rate_per_project) : assignForm.agreed_amount})}
                        data-testid={`suggestion-${s.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#F5A623]/20 text-[#F5A623]' : 'bg-white/5 text-zinc-500'}`}>
                            {i + 1}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{s.name}</p>
                            <p className="text-xs text-zinc-500">{s.speciality} • {s.reliability_score}% reliable • {s.active_projects} active</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#F5A623] text-sm font-medium">{s.match_score}pts</p>
                          <p className="text-xs text-zinc-500">{s.reasons[0]}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Select Freelancer *</Label>
                <Select value={assignForm.freelancer_id} onValueChange={(v) => setAssignForm({...assignForm, freelancer_id: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="select-freelancer">
                    <SelectValue placeholder="Choose a freelancer" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {freelancers.map(f => (
                      <SelectItem key={f.id} value={f.id} className="text-white hover:bg-white/10">
                        {f.name} — {f.speciality} ({f.reliability_score}% reliable)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Raw Files Handover *</Label>
                  <Input
                    type="date"
                    value={assignForm.raw_files_handover_date}
                    onChange={(e) => setAssignForm({...assignForm, raw_files_handover_date: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    data-testid="handover-date-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Internal Deadline *</Label>
                  <Input
                    type="date"
                    value={assignForm.internal_deadline}
                    onChange={(e) => setAssignForm({...assignForm, internal_deadline: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    data-testid="internal-deadline-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Agreed Amount (₹) *</Label>
                <Input
                  type="number"
                  value={assignForm.agreed_amount}
                  onChange={(e) => setAssignForm({...assignForm, agreed_amount: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="15000"
                  data-testid="agreed-amount-input"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
                <Textarea
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="Any instructions for the freelancer..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                  Cancel
                </Button>
                <Button 
                  onClick={handleAssignFreelancer}
                  className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                  data-testid="submit-assign-btn"
                >
                  Assign Freelancer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsPage;
